import React, { useState, useCallback, useContext } from "react";
import {
  Box,
  makeStyles,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@material-ui/core";
import Card from "../../../../components/Card/Card.js";
import CardBody from "../../../../components/Card/CardBody.js";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import PropTypes from "prop-types";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import AuthContext from "../../../../connections/auth/AuthProvider";
import { useTranslation } from "react-i18next";
import PriceRoute from "../BoardTab/PriceRoute/PriceRoute.js";
import FilterSystem from "../../sharedComponents/FilterSystem/FilterSystem.js";

const priceRouteTabStyle = (theme, clientColor) => ({
  ...customSelectStyle(theme, clientColor),
  containerBox: {
    padding: theme.spacing(0),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    height: "100%",
    overflow: "hidden",
  },
  controlCard: {
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    padding: theme.spacing(0),
  },
  controlCardBody: {
    padding: 0,
  },
  controlCardContent: {
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flex: "1",
    overflow: "visible",
  },
  addScenarioBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginLeft: theme.spacing(2),
    flex: "0 0 auto",
  },
  selectFormControl: {
    marginBottom: theme.spacing(1),
    minWidth: 140,
    margin: "7px 0 7px 0 !important",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: grayColor[4] + "!important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: clientColor + "!important",
      },
    },
  },
  filtersBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flex: "1",
    overflow: "visible",
  },
  divider: {
    width: "1px",
    backgroundColor: grayColor[4],
    alignSelf: "stretch",
    margin: `${theme.spacing(1)}px 0`,
  },
  contentArea: {
    flex: 1,
    overflow: "hidden",
  },
});

PriceRouteTab.propTypes = {
  scenarios: PropTypes.array.isRequired,
  folders: PropTypes.array,
  isLoading: PropTypes.bool,
};

PriceRouteTab.displayName = "PriceRouteTab";

export default function PriceRouteTab({
  scenarios,
  folders = [],
  isLoading = false,
}) {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;
  const useStyles = makeStyles((theme) =>
    priceRouteTabStyle(theme, clientColor),
  );
  const classes = useStyles();

  const T_PREFIX = "rgm.cockpit";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  // Independent state for PriceRoute tab
  const [selectedVolume, setSelectedVolume] = useState(
    "input_sellthrough_volume_sales",
  );
  const [selectedFilters, setSelectedFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [selectedSolverStatus, setSelectedSolverStatus] = useState("");
  const [solverStatusOptions, setSolverStatusOptions] = useState([]);

  const VOLUME_WEIGHT_OPTIONS = [
    {
      value: "input_sellthrough_volume_sales",
      label: t("overview.volume_options.volume_sellthrough"),
    },
    {
      value: "input_sellin_volume_sales",
      label: t("overview.volume_options.volume_sellin"),
    },
    { value: "1", label: t("overview.volume_options.not_weighted") },
  ];

  const filterDimensions = [
    { key: "products", label: t("overview.filter_dimensions.products") },
    { key: "brands", label: t("overview.filter_dimensions.brands") },
    { key: "channels", label: t("overview.filter_dimensions.channels") },
    { key: "regions", label: t("overview.filter_dimensions.regions") },
    { key: "clients", label: t("overview.filter_dimensions.clients") },
  ];

  const handleFiltersChange = useCallback((newFilters) => {
    setSelectedFilters(newFilters);
  }, []);

  const handleFilterOptionsChange = useCallback((options) => {
    setFilterOptions(options);
    // Also extract solver status options if available
    if (options.solver_status) {
      setSolverStatusOptions(options.solver_status);
    }
  }, []);

  return (
    <Box className={classes.containerBox}>
      {/* Control Bar */}
      <Card className={classes.controlCard}>
        <CardBody className={classes.controlCardBody}>
          <Box className={classes.controlCardContent}>
            {/* Left Section: Volume + Solver Status + Filters */}
            <Box className={classes.leftSection}>
              {/* Volume Selector */}
              <Box className={classes.addScenarioBox}>
                <FormControl
                  className={classes.selectFormControl}
                  style={{ minWidth: 140, marginBottom: 0 }}
                >
                  <InputLabel id="volume-selector-label-priceroute">
                    {t("overview.volume_weight_label")}
                  </InputLabel>
                  <Select
                    labelId="volume-selector-label-priceroute"
                    value={selectedVolume}
                    onChange={(e) => setSelectedVolume(e.target.value)}
                    label={t("overview.volume_weight_label")}
                    disabled={Object.keys(filterOptions).length === 0}
                  >
                    {VOLUME_WEIGHT_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Solver Status Dropdown */}
              <Box className={classes.addScenarioBox}>
                <FormControl
                  className={classes.selectFormControl}
                  style={{ minWidth: 140, marginBottom: 0 }}
                >
                  <InputLabel
                    id="solver-status-selector-label-priceroute"
                    shrink
                  >
                    {t("overview.solver_status_label")}
                  </InputLabel>
                  <Select
                    labelId="solver-status-selector-label-priceroute"
                    value={selectedSolverStatus}
                    onChange={(e) => setSelectedSolverStatus(e.target.value)}
                    label={t("overview.solver_status_label")}
                    displayEmpty
                    disabled={Object.keys(filterOptions).length === 0}
                    renderValue={(selected) => {
                      if (!selected || selected === "") {
                        return <em>{t("overview.all_statuses")}</em>;
                      }
                      return selected;
                    }}
                  >
                    <MenuItem value="">
                      <em>{t("overview.all_statuses")}</em>
                    </MenuItem>
                    {solverStatusOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box className={classes.divider} />

              <Box className={classes.filtersBox}>
                <FilterSystem
                  dimensions={filterDimensions}
                  filterOptions={filterOptions}
                  selectedFilters={selectedFilters}
                  onFiltersChange={handleFiltersChange}
                  clientColor={clientColor}
                  disabled={Object.keys(filterOptions).length === 0}
                />
              </Box>
            </Box>
          </Box>
        </CardBody>
      </Card>

      {/* PriceRoute Content */}
      <Box className={classes.contentArea}>
        <PriceRoute
          scenarios={scenarios}
          folders={folders}
          isLoading={isLoading}
          filters={selectedFilters}
          selectedVolume={selectedVolume}
          selectedSolverStatus={selectedSolverStatus}
          onFilterOptionsChange={handleFilterOptionsChange}
        />
      </Box>
    </Box>
  );
}
